import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { EntityType } from '@/types/link';
import { useRelatedEntities } from '@/hooks/useRelatedEntities';
import { RelationshipBadge } from './RelationshipBadge';
import { EntitySelectorDialog } from './EntitySelectorDialog';
import { useLinkStore } from '@/stores/linkStore';
import { Plus, Trash2, Network } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface LinkManagerProps {
  entityType: EntityType;
  entityId: string;
  entityName: string;
  allowedTypes?: EntityType[];
  showHeader?: boolean;
}

export function LinkManager({ 
  entityType, 
  entityId, 
  entityName,
  allowedTypes = ['asset', 'site', 'zone', 'system', 'risk', 'control', 'supplier', 'incident', 'task', 'evidence'],
  showHeader = true 
}: LinkManagerProps) {
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState<{ id: string; name: string } | null>(null);
  
  const related = useRelatedEntities(entityType, entityId);
  const { createLink, bulkCreateLinks, deleteLink, getLinkedEntities, loadLinks } = useLinkStore();

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const handleAddLinks = async (targetType: EntityType, targetIds: string[]) => {
    if (targetIds.length === 1) {
      await createLink({
        source_type: entityType,
        source_id: entityId,
        target_type: targetType,
        target_id: targetIds[0],
      });
    } else {
      await bulkCreateLinks(entityType, entityId, targetType, targetIds);
    }
    await loadLinks();
  };

  const handleDeleteLink = async () => {
    if (!linkToDelete) return;
    
    const links = getLinkedEntities(entityType, entityId);
    const linkRecord = links.find(l => 
      (l.source_type === entityType && l.target_id === linkToDelete.id) ||
      (l.target_type === entityType && l.source_id === linkToDelete.id)
    );
    
    if (linkRecord) {
      await deleteLink(linkRecord.id);
      await loadLinks();
    }
    
    setLinkToDelete(null);
    setDeleteDialogOpen(false);
  };

  const confirmDelete = (id: string, name: string) => {
    setLinkToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const allRelated = [
    ...related.assets,
    ...related.sites,
    ...related.zones,
    ...related.systems,
    ...related.risks,
    ...related.controls,
    ...related.suppliers,
    ...related.incidents,
    ...related.tasks,
    ...related.evidence,
    ...related.shared_infrastructure,
  ];

  const groupedByType = allRelated.reduce((acc, entity) => {
    if (!acc[entity.type]) acc[entity.type] = [];
    acc[entity.type].push(entity);
    return acc;
  }, {} as Record<EntityType, typeof allRelated>);

  return (
    <>
      <Card>
        {showHeader && (
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Network className="w-5 h-5" />
                  Relationships
                </CardTitle>
                <CardDescription>
                  Entities linked to {entityName}
                </CardDescription>
              </div>
              <Button onClick={() => setSelectorOpen(true)} size="sm">
                <Plus className="w-4 h-4 mr-1" />
                Add Link
              </Button>
            </div>
          </CardHeader>
        )}
        
        <CardContent>
          {related.loading ? (
            <p className="text-sm text-muted-foreground">Loading relationships...</p>
          ) : allRelated.length === 0 ? (
            <div className="text-center py-8">
              <Network className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground mb-4">
                No relationships yet
              </p>
              <Button onClick={() => setSelectorOpen(true)} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-1" />
                Add First Link
              </Button>
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="space-y-4">
                {Object.entries(groupedByType).map(([type, entities]) => (
                  <div key={type}>
                    <h4 className="text-sm font-medium mb-2 capitalize">
                      {type.replace('_', ' ')} ({entities.length})
                    </h4>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {entities.map(entity => (
                        <div key={entity.id} className="flex items-center gap-1">
                          <RelationshipBadge
                            entityType={entity.type}
                            entityId={entity.id}
                            entityName={entity.name}
                            metadata={entity.metadata}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => confirmDelete(entity.id, entity.name)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <Separator />
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      <EntitySelectorDialog
        open={selectorOpen}
        onOpenChange={setSelectorOpen}
        onSelect={handleAddLinks}
        allowedTypes={allowedTypes}
        multiSelect={true}
        title={`Link entities to ${entityName}`}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Relationship?</AlertDialogTitle>
            <AlertDialogDescription>
              Remove the link to "{linkToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteLink}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
